import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { usePortalAuth } from '../../hooks/usePortalAuth';
import {
	getStorefrontAccountDashboard,
	getStorefrontPromos,
	type PortalDashboardWidget,
} from '../../services/portalDashboardService';
import { MD_COL_SPAN, StorefrontWidgetDispatch } from './StorefrontDashboard';

/**
 * Storefront ACCOUNT-PAGE dashboard. Resolves the account design for the
 * active portal_site (admin-authored in CRM Settings → Ecommerce →
 * Account Page Designs) and renders it through the SAME row-by-position
 * grid + `StorefrontWidgetDispatch` the homepage uses — account widgets
 * (snapshot, recent orders / invoices / transactions / statements,
 * payment methods, profile) are ordinary STOREFRONT widgets, so the same
 * dispatch handles them, and any homepage widget an admin drops here works
 * too.
 *
 * Returns null when no account design is assigned (and no tenant-default
 * exists), so `AccountPage` falls back to its hardcoded card layout — no
 * portal is forced to adopt a design.
 *
 * The account page is authenticated (gated by `AccountPage`), so unlike
 * the homepage there are no anon / catalog-disabled widget filters here;
 * each widget still self-hides (returns null) when it has nothing to show,
 * and the CSS-grid row collapses around it.
 */
export function AccountDashboard(): JSX.Element | null {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const queryClient = useQueryClient();

	const dashboardQuery = useQuery({
		// Distinct key from the homepage (['portal','dashboard']) so the two
		// designs never cross-contaminate the query cache.
		queryKey: ['portal', 'account-page'],
		queryFn: () => getStorefrontAccountDashboard(token),
		staleTime: 60_000,
	});

	// Promos are fetched so a promo widget placed on the account page still
	// renders; account-only designs simply never read this list.
	const promosQuery = useQuery({
		queryKey: ['portal', 'dashboard', 'promos'],
		queryFn: () => getStorefrontPromos(token),
		staleTime: 30_000,
	});

	const refetchPromosForStaleImage = (): void => {
		void queryClient.invalidateQueries({
			queryKey: ['portal', 'dashboard', 'promos'],
		});
	};

	const dashboard = dashboardQuery.data?.data ?? null;
	const eligiblePromos = promosQuery.data?.data ?? [];

	// Group widgets into rows by position.y (side-by-side share a row),
	// sorted within a row by position.x — same layout model as the homepage.
	const widgetRows = useMemo<Array<Array<PortalDashboardWidget>>>(() => {
		if (dashboard == null) return [];
		const byRow = new Map<number, Array<PortalDashboardWidget>>();
		for (const w of dashboard.widgets) {
			const y = w.position.y;
			const arr = byRow.get(y) ?? [];
			arr.push(w);
			byRow.set(y, arr);
		}
		return Array.from(byRow.entries())
			.sort((a, b) => a[0] - b[0])
			.map(([, ws]) => ws.sort((a, b) => a.position.x - b.position.x));
	}, [dashboard]);

	if (dashboardQuery.isLoading) {
		return (
			<div className="animate-pulse space-y-4">
				<div className="h-24 bg-brand-input rounded-xl" />
				<div className="h-40 bg-brand-input rounded-xl" />
			</div>
		);
	}

	if (dashboard == null || widgetRows.length === 0) {
		return null;
	}

	return (
		<div
			className="space-y-6"
			data-testid="account-dashboard"
			aria-label={t(dashboard.name)}
		>
			{widgetRows.map((row, rowIdx) => (
				<div
					key={rowIdx}
					className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6"
				>
					{row.map((w) => (
						<div
							key={w.id}
							className={
								MD_COL_SPAN[Math.min(Math.max(w.position.width, 1), 4)]
							}
						>
							<StorefrontWidgetDispatch
								widget={w}
								eligiblePromos={eligiblePromos}
								onLikelyStalePromoImage={refetchPromosForStaleImage}
							/>
						</div>
					))}
				</div>
			))}
		</div>
	);
}
